import React, { useState, useContext } from 'react'
import { CognitoIdentityProviderClient, InitiateAuthCommand } from "@aws-sdk/client-cognito-identity-provider"
import CloudformationOutputs from '../CloudformationOutputs.json'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import Password from './auth_formfields/Password'
import Text from './auth_formfields/Text'
import { Link, useNavigate } from 'react-router-dom'

type Props = {
    authChallenge: ObjectStringKeyAnyValue | null
    setAuthChallenge: React.Dispatch<React.SetStateAction<any>>
    setShowForm: React.Dispatch<React.SetStateAction<any>>
}


function LoginForm({
    authChallenge,
    setAuthChallenge,
    setShowForm
}: Props) {

    const {
        socket,
        setShowModal,
        sendMessageToWebsocket,
        setShowLoginModal,
        getUserStats,
        t
    } = useContext(GlobalContext)

    const navigate = useNavigate()
    const [formFields, setFormFields] = useState<ObjectStringKeyAnyValue>({})
    const [formErrors, setFormErrors] = useState<ObjectStringKeyAnyValue>({})


    const validate = () => {
        let isValid = true
        const emailRegex = new RegExp("^.+@.+")
        const newFormErrors: ObjectStringKeyAnyValue = {}

        if (
            !formFields["emailAddress"] ||
            !emailRegex.test(formFields["emailAddress"])
        ) {
            isValid = false
            newFormErrors["emailAddress"] = t('Auth.LoginForm.enterEmail')
        }

        if (!formFields['password']) {
            isValid = false
            newFormErrors["password"] = t('Auth.LoginForm.enterPassword')

        }
        setFormErrors(newFormErrors)
        return isValid
    }




    const validateAndSave = () => {
        const isValid = validate()
        if (isValid) {
            setShowLoginModal(true)
            const client = new CognitoIdentityProviderClient({ region: CloudformationOutputs['AWSRegion'] })
            const username = formFields["emailAddress"].toLowerCase()
            const password = formFields['password']
            const command = new InitiateAuthCommand({
                AuthFlow: "USER_PASSWORD_AUTH",
                ClientId: CloudformationOutputs['UserPoolClientId'],
                AuthParameters: {
                    USERNAME: username,
                    PASSWORD: password,
                }
            })
            client.send(command)
                .then((data: any) => {

                    const idTokenFromCognito = data.AuthenticationResult?.IdToken
                    const accessTokenFromCognito = data.AuthenticationResult?.AccessToken
                    const refreshTokenFromCognito = data.AuthenticationResult?.RefreshToken

                    // if there is a refresh token, save it locally
                    if (refreshTokenFromCognito) {
                        //console.log(`🥬 saving refresh token: ${refreshTokenFromCognito}`)
                        localStorage.setItem('localRefreshToken', refreshTokenFromCognito)
                    }

                    // handle new password required
                    if (
                        data.ChallengeName === "NEW_PASSWORD_REQUIRED" &&
                        data.ChallengeParameters &&
                        data.ChallengeParameters.USER_ID_FOR_SRP
                    ) {
                        //! HANDLE SETTING A NEW PASSWORD
                        console.log("New password required")
                        setShowModal(null)
                        setShowLoginModal(false)
                        setAuthChallenge(data)
                    }

                    // handle sucessful login
                    else if (idTokenFromCognito && accessTokenFromCognito) {
                        const payloadObj: { [index: string]: string } = {
                            action: "login",
                            loggingInFromApp: "mobilechecklist",
                            idToken: idTokenFromCognito,
                            accessToken: accessTokenFromCognito,
                            userStats: getUserStats()
                        }
                        const payload = JSON.stringify(payloadObj)
                        //console.log(`sending tokens to websocket`)
                        localStorage.setItem('localIdToken', idTokenFromCognito)
                        localStorage.setItem('localAccessToken', accessTokenFromCognito)
                        sendMessageToWebsocket(payload)
                    } else {
                        setShowLoginModal(false)
                        setShowModal({ "error": t('Auth.LoginForm.loginFailed') })
                    }
                })
                .catch((error: any) => {
                    setShowModal(null)
                    setShowLoginModal(false)
                    console.error("Login failed", error)
                    setShowModal({ "error": t('Auth.LoginForm.loginFailed') })
                })
        }
    }




    return <div className={`w-full flex flex-col gap-4`}>

        <h3 className={`font-righteous text-2xl text-brand-dark`}>
            {t('Auth.LoginForm.logIn')}
        </h3>

        <Text
            labelText={t('Auth.LoginForm.emailAddress')}
            formFields={formFields}
            setFormFields={setFormFields}
            formErrors={formErrors}
            fieldName={'emailAddress'}
            required={true}
            size={'big'}
            center={true}
        />

        <Password
            labelText={t('Auth.LoginForm.password')}
            formFields={formFields}
            formErrors={formErrors}
            setFormFields={setFormFields}
            fieldName={'password'}
            required={true}
            size={'big'}
            center={true}
        />

        <div className={`w-full flex flex-row gap-4`}>
            
            <div
                onClick={() => setShowForm(null)}
                className={`w-full inline-block font-bold text-lg rounded hover:opacity-90 bg-gray-400 text-gray-700 text-lg px-5 py-3 cursor-pointer`}
            >{t('Buttons.cancel')}</div>

            <Button
                onClick={validateAndSave}
                text={t('Buttons.next')}
                size={'big'}
                sameWindow={true}
                disabled={!socket || !formFields['emailAddress'] || !formFields['password']}
                variant={socket ? 'secondary' : 'gray'}
                id={socket ? `continueButton` : 'inactive'}
            />
        </div>

        <Link to={`/forgotPassword`} className={`mb-4 text-sm underline hover:opacity-80 cursor-pointer`}>{t('Auth.LoginForm.forgotYourPassword')}</Link>
    </div>
}
export default LoginForm